import { formatShareBps } from "@loot-os/api-contract";
import { Badge } from "@loot-os/ui/components/badge";
import { Button, buttonVariants } from "@loot-os/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { toast } from "@loot-os/ui/components/sonner";
import { formatNumber } from "@loot-os/ui/lib/format";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Outlet } from "@tanstack/react-router";
import { ExternalLinkIcon, RefreshCwIcon } from "lucide-react";
import { ErrorAlert } from "../components/ErrorAlert";
import { shopifyAppUrl } from "../lib/shopifyStore";
import { useTRPC } from "../lib/trpc";
import { useShopifyShop } from "../lib/useShopifyShop";
import { useTenantRole } from "../lib/useTenantRole";

/**
 * The route under every Shopify page (docs/shopify.md), for one environment: which shop is
 * linked, then, once one is, the section beneath: the overview at /shopify, then Sales,
 * Appearance and Fees, the Shopify item's children in `TENANT_NAV`. A section renders only while a shop is
 * linked; until then every section says how a store links itself (an App Store install).
 */
export function ShopifyLayout() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { can } = useTenantRole();
  const { status, shop, linked, shopHandle } = useShopifyShop();

  const syncCatalog = useMutation(
    trpc.shopify.syncCatalog.mutationOptions({
      onSuccess: (data) => {
        void queryClient.invalidateQueries({ queryKey: trpc.shopify.pathKey() });
        toast.success(
          data.complete
            ? `Synced ${formatNumber(data.variants)} variants.`
            : "Still syncing; it finishes within a few minutes.",
        );
      },
    }),
  );

  const appClientId = status.data?.appClientId ?? null;
  if (status.error) return <ErrorAlert message={status.error.message} />;
  if (!status.data) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="flex max-w-2xl min-w-0 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight">Shopify</h1>
            {shop && (
              <Badge variant={shop.status === "active" ? "secondary" : "outline"}>
                {shop.status}
              </Badge>
            )}
            {shop?.status === "active" && (
              <Badge variant={shop.billingStatus === "active" ? "secondary" : "outline"}>
                {shop.billingStatus === "active" && shop.billingRateBps != null
                  ? `Plan: ${formatShareBps(shop.billingRateBps)}`
                  : `Plan: ${shop.billingStatus}`}
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            Sell this environment's boxes as products in a Shopify store. Shoppers reveal them on
            the store's own domain; a claim ships as a Shopify order and a sell-back pays store
            credit.
          </p>
        </div>
        {linked && (
          <div className="flex flex-wrap items-center gap-2">
            {shopHandle && appClientId && (
              <a
                href={shopifyAppUrl(shopHandle, appClientId)}
                target="_blank"
                rel="noreferrer"
                className={buttonVariants({ variant: "outline" })}
              >
                Open in Shopify
                <ExternalLinkIcon data-icon="inline-end" />
              </a>
            )}
            {can("admin") && (
              <Button
                type="button"
                variant="outline"
                onClick={() => syncCatalog.mutate()}
                disabled={syncCatalog.isPending}
              >
                <RefreshCwIcon
                  data-icon="inline-start"
                  className={syncCatalog.isPending ? "animate-spin" : undefined}
                />
                {syncCatalog.isPending ? "Syncing…" : "Sync catalog"}
              </Button>
            )}
          </div>
        )}
      </header>

      {!linked && (
        <Card className="max-w-2xl">
          <CardHeader>
            <CardTitle>
              {shop ? `${shop.shopDomain} is ${shop.status}` : "No store linked"}
            </CardTitle>
            <CardDescription>
              {shop
                ? "The store comes back as soon as its owner installs the app again."
                : "A Shopify store links itself: its owner installs Loot Boxes for Shopify from the Shopify App Store, and the install sets up its own tenant."}
            </CardDescription>
          </CardHeader>
          {!appClientId && (
            <CardContent>
              <p className="text-sm text-muted-foreground">
                The Shopify app is not configured on this stage.
              </p>
            </CardContent>
          )}
        </Card>
      )}

      {linked && <Outlet />}
    </div>
  );
}
